"use client";
import React from "react";
import { useSelector } from "react-redux";
import { RichText } from "prismic-reactjs";
import Link from "next/link";
import snakeCase from "lodash/snakeCase";
import { NavHashLink } from "react-router-hash-link";

import zendeskService from "@stadium/services/zendesk";

import { getShippingCountry } from "@sendmagic/prismic-nextjs/src/selectors/shipping";

import { getCookieByName } from "@stadium/helpers/cookie";
import { baseCountryLanguage, showChatbotWindow } from "@stadium/constants";

import { urlResolver } from "@sendmagic/prismic-nextjs/src/constants/prismic";
import { labelClassesMapping } from "@sendmagic/prismic-nextjs/src/constants/labels";
import { SWAGMAGIC_TOP_TARGET_URL } from "@stadium/constants/catalog";

const REACT_PROJECT_TYPE = "react";

const defaultObj = {};

export const customPrismicLink = (type, element, content) => (
  <PrismicLinkTag {...element.data} key={element.data.id} urlText={content} />
);

export const textResolver = (text, options = {}, labelClass = "") => {

  const textContent = Array.isArray(text) ? (
    <RichText
      render={text}
      htmlSerializer={(type, element, content) => {
        const { convertLabelToModular = false, convertToNormalText = false } = options;
        switch (type) {
          case "preformatted":
            return (
              <div dangerouslySetInnerHTML={{ __html: element.text }} className={`${labelClass}`} />
            );
          case "hyperlink":
            return customPrismicLink(type, element, content);
          case "label": {
            if (convertLabelToModular) {
              const className = `${labelClassesMapping[element.data.label]} ${labelClass}`;
              return <span dangerouslySetInnerHTML={{ __html: content }} className={className} />;
            }
            return null;
          }
          case "paragraph": {
            if (convertToNormalText) {
              return element.text;
            }
            return null;
          }
          default:
            return null;
        }
      }}
    />
  ) : (
    text
  );

  return (
    <>
      {textContent}
      {options.openInNewTab && <span className="new-tab-icon" />}
    </>
  );

}
export const plainText = (text) => RichText.asText(text);

export const PrismicLinkTag = ({
  url,
  urlText,
  isActive,
  activeClassName,
  convertLabelToModular = false,
  country,
  prefetch = false,
  enableNavHashLink = false,
  labelClass = "",
  linkNewTabIconClassName = "",
  target: propsTarget,
  ...rest
}) => {
  const { iso: countryIso } = country || defaultObj;
  const { show: showIcon } = zendeskService;

  const targetTop = SWAGMAGIC_TOP_TARGET_URL.test(url) ? "_top" : "";

  const target = propsTarget || targetTop;

  let formattedUrl = url
    ?.replace(/:countryIso\??/, countryIso?.toLowerCase() || "")
    .replace(/:language\??/, baseCountryLanguage);

  const relativeUrl = urlResolver(formattedUrl);

  if (relativeUrl == showChatbotWindow) {
    return (
      <a href="#" onClick={() => showIcon()} {...rest}>
        {textResolver(urlText, { convertLabelToModular }, labelClass)}
      </a>
    );
  }

  if (formattedUrl === relativeUrl) {
    formattedUrl = formattedUrl.replace(/@@\w*/g, (str) => {
      str = str.replace("@@", "");
      return getCookieByName(str) ? `${snakeCase(str)}=${getCookieByName(str)}` : "";
    });
    if (process.env.PROJECT_APP_TYPE === REACT_PROJECT_TYPE) {
      if (enableNavHashLink) {
        return (
          <NavHashLink
            to={relativeUrl}
            isActive={isActive}
            activeClassName={activeClassName}
            target={target}
            {...rest}
          >
            {textResolver(urlText, { convertLabelToModular }, labelClass)}
          </NavHashLink>
        );
      }

      return (
        <a href={formattedUrl} target={target} {...rest} className={`${rest.className} ${target === "_blank" ? linkNewTabIconClassName : ""}`}>
          {textResolver(urlText, { convertLabelToModular, openInNewTab: target === "_blank" }, labelClass)}
        </a>
      );
    }
    return (
      <Link href={url} prefetch={prefetch} target={target} {...rest} className={`${rest.className} ${target === "_blank" ? linkNewTabIconClassName : ""}`} rel="noopener">
        {textResolver(urlText, { convertLabelToModular, openInNewTab: target === "_blank" }, labelClass)}
      </Link>
    );
  }
  if (process.env.PROJECT_APP_TYPE === REACT_PROJECT_TYPE) {
    return (
      <NavHashLink
        to={relativeUrl}
        isActive={isActive}
        activeClassName={activeClassName}
        target={target}
        {...rest}
      >
        {textResolver(urlText, { convertLabelToModular }, labelClass)}
      </NavHashLink>
    );
  }
  return (
    <Link href={relativeUrl} prefetch={prefetch} isActive={isActive} target={target} {...rest}>
      {textResolver(urlText, { convertLabelToModular }, labelClass)}
    </Link>
  );
};

const PrismicLinkTagWithSelector = (props) => {
  const country = useSelector(getShippingCountry);

  return <PrismicLinkTag {...props} country={country} />;
};

export default PrismicLinkTagWithSelector;
